/**
 * The loading state: the shape of what is coming (`skeleton-bar` in DESIGN.md).
 *
 * It replaces "Loading these questions…" in a bordered box, which was a
 * sentence about waiting where the page should have been. When the rows
 * arrived they pushed everything below them down, and the eye had to find its
 * place again. A skeleton holds roughly the space the content will take, so
 * the page settles instead of jumping (`frontend.md` §7).
 *
 * Three arrangements, one per kind of surface, rather than a skeleton drawn
 * per screen: close enough in shape, and one place to keep them in step.
 */
type Shape = "rows" | "report" | "text";

/**
 * Staggered, so the bars read as text of varying length, not as a grid. Each
 * width appears once, so it doubles as the key.
 */
const WIDTHS = ["72%", "54%", "64%", "46%", "68%", "58%", "50%", "62%"] as const;

export function Skeleton({
  shape,
  label,
}: {
  readonly shape: Shape;
  /** What is loading, for a screen reader: "Loading these questions". */
  readonly label: string;
}) {
  return (
    <div className={`skeleton skeleton-${shape}`} role="status" aria-label={label}>
      {shape === "rows" ? <Rows /> : shape === "report" ? <Report /> : <Text />}
    </div>
  );
}

function Rows() {
  return (
    <>
      <div className="skeleton-row skeleton-row-head">
        <Bar width="18%" />
        <Bar width="10%" />
        <Bar width="10%" />
      </div>
      {WIDTHS.map((width) => (
        <div className="skeleton-row" key={width}>
          <Bar width={width} />
          <Bar width="8%" />
          <Bar width="8%" />
        </div>
      ))}
    </>
  );
}

function Report() {
  return (
    <>
      <Bar width="24%" />
      <div className="skeleton-report-grid">
        <div className="skeleton-block" />
        <div className="skeleton-block" />
      </div>
    </>
  );
}

function Text() {
  return (
    <>
      <Bar width="32%" />
      {WIDTHS.slice(0, 6).map((width) => (
        <Bar key={width} width={width} />
      ))}
    </>
  );
}

function Bar({ width }: { readonly width: string }) {
  return <span className="skeleton-bar" style={{ width }} />;
}

/*
 * The pieces a screen draws its own skeleton from (DESIGN.md App States): its
 * real headings and labels, and these only where a value from the server goes.
 * A generic arrangement above is close to a page's shape at best, and the page
 * then jumps into a different one (Abhishek, 2026-10-10, on Profile).
 */

/**
 * A line of text still on its way. `as` is the class of the text it stands in
 * for (`field-hint`, `settings-section-title`…), so the line takes that text's
 * size, margins and line height, and the page does not move when it arrives.
 */
export function SkeletonLine({
  width,
  as = "",
}: {
  readonly width: string;
  readonly as?: string;
}) {
  return (
    <span className={`skeleton-line ${as}`.trim()}>
      <Bar width={width} />
    </span>
  );
}

/** A text box whose value is still on its way: the box itself, a bar inside. */
export function SkeletonInput() {
  return (
    <span className="text-input skeleton-input" aria-hidden="true">
      <Bar width="40%" />
    </span>
  );
}
